import * as React from "react";
import { Globe, Lock, Users, type LucideIcon } from "lucide-react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/cn";

/**
 * VisibilityBadge — opaque, reusable chip for an entity's visibility
 * (Public / Private / Team). Centralises the icon + colour + label so cards,
 * detail headers, table rows, and any access-controlled surface share a
 * single source of truth — without re-defining the visibility lookup.
 *
 * Two appearances:
 * - **`solid`** (default) — opaque tone-tinted background. Designed for cover
 *   overlays where the badge sits on a photographic image.
 * - **`soft`** — pale tinted chip. Use in dense content rows / settings UIs
 *   where the louder solid styling is too heavy.
 */

export type Visibility = "public" | "private" | "team" | string;

interface VisibilityMeta {
  label: string;
  Icon: LucideIcon;
  /** Internal tone key — drives the background + border + text colour. */
  tone: "success" | "muted" | "info";
}

const VISIBILITY_META: Record<string, VisibilityMeta> = {
  public: { label: "Public", Icon: Globe, tone: "success" },
  private: { label: "Private", Icon: Lock, tone: "muted" },
  team: { label: "Team", Icon: Users, tone: "info" },
};

const FALLBACK_META: VisibilityMeta = { label: "Restricted", Icon: Lock, tone: "muted" };

export const getVisibilityMeta = (visibility: Visibility): VisibilityMeta =>
  VISIBILITY_META[(visibility ?? "").toLowerCase()] ?? FALLBACK_META;

const visibilityBadgeVariants = cva(
  "inline-flex items-center gap-1 whitespace-nowrap font-medium rounded-full border shadow-sm",
  {
    variants: {
      appearance: {
        solid: "",
        soft: "",
        overlay: "backdrop-blur-sm",
      },
      tone: {
        success: "",
        muted: "",
        info: "",
      },
      size: {
        sm: "px-2 py-0.5 text-[10px] [&_svg]:size-3",
        md: "px-2.5 py-1 text-xs [&_svg]:size-3.5",
        lg: "px-3 py-1.5 text-sm [&_svg]:size-4",
      },
    },
    compoundVariants: [
      // Solid — opaque token bg + token foreground.
      { appearance: "solid", tone: "success", class: "bg-success text-success-foreground border-success/40" },
      { appearance: "solid", tone: "info", class: "bg-info text-info-foreground border-info/40" },
      // "muted" private is a neutral solid — secondary token bg + foreground.
      {
        appearance: "solid",
        tone: "muted",
        class: "bg-secondary text-secondary-foreground border-border",
      },
      // Soft — semi-transparent token bg with token foreground.
      { appearance: "soft", tone: "success", class: "bg-success/15 text-success border-success/30" },
      { appearance: "soft", tone: "info", class: "bg-info/15 text-info border-info/30" },
      { appearance: "soft", tone: "muted", class: "bg-muted text-muted-foreground border-border" },
      // Overlay — glassy card-color chip distinguished only by icon. Designed
      // for cover-overlay use where token-coloured tints would compete with
      // the photographic image underneath. Matches the pre-primitive
      // translucent badge style users liked on visibility chips.
      {
        appearance: "overlay",
        tone: "success",
        class: "bg-background/85 text-foreground border-border",
      },
      {
        appearance: "overlay",
        tone: "info",
        class: "bg-background/85 text-foreground border-border",
      },
      {
        appearance: "overlay",
        tone: "muted",
        class: "bg-background/85 text-foreground border-border",
      },
    ],
    defaultVariants: { appearance: "solid", size: "md", tone: "muted" },
  },
);

export interface VisibilityBadgeProps
  extends Omit<React.HTMLAttributes<HTMLSpanElement>, "children">,
    Pick<VariantProps<typeof visibilityBadgeVariants>, "appearance" | "size"> {
  visibility: Visibility;
  /** Skip the icon, render label only. */
  noIcon?: boolean;
}

export const VisibilityBadge = React.forwardRef<HTMLSpanElement, VisibilityBadgeProps>(
  ({ visibility, appearance = "solid", size = "md", noIcon = false, className, ...rest }, ref) => {
    const meta = getVisibilityMeta(visibility);
    const Icon = meta.Icon;
    return (
      <span
        ref={ref}
        className={cn(visibilityBadgeVariants({ appearance, size, tone: meta.tone }), className)}
        {...rest}
      >
        {noIcon ? null : <Icon aria-hidden />}
        <span>{meta.label}</span>
      </span>
    );
  },
);
VisibilityBadge.displayName = "VisibilityBadge";
